import { useCallback, useRef, useEffect } from 'react'
import logger from '../utils/logger'

/**
 * Custom hook for handling realtime vote events with optimistic update tracking
 * @param {Function} setChoices - Function to update choices state
 * @param {string} pollId - The poll ID for logging
 * @param {string} deviceId - Current device ID for filtering own votes
 * @param {Object} choicesRef - Ref to current choices array (for event filtering)
 * @returns {Object} Vote event handlers and pending refs
 */
function useRealtimeVotes(setChoices, pollId, deviceId, choicesRef) {
    const pendingVotesRef = useRef(new Set())
    const pendingRemovalsRef = useRef(new Set())
    const voteIdToChoiceIdRef = useRef(new Map())
    const deviceIdRef = useRef(deviceId)

    // Keep deviceIdRef in sync to prevent stale closure issues
    useEffect(() => {
        deviceIdRef.current = deviceId
    }, [deviceId])

    const handleNewVote = useCallback((choiceId, voteType = 'upvote') => {
        setChoices(prev => {
            const choice = prev.find(c => c.id === choiceId)
            if (!choice) {
                logger.warn('Realtime: Received vote for unknown choice', { pollId, choiceId })
                return prev
            }

            const updated = prev.map(c => {
                if (c.id !== choiceId) return c

                const currentUpvotes = c.upvote_count || 0
                const currentDownvotes = c.downvote_count || 0

                let newUpvotes = currentUpvotes
                let newDownvotes = currentDownvotes

                if (voteType === 'upvote') {
                    newUpvotes = currentUpvotes + 1
                } else {
                    newDownvotes = currentDownvotes + 1
                }

                const newNetVotes = newUpvotes - newDownvotes

                return {
                    ...c,
                    upvote_count: newUpvotes,
                    downvote_count: newDownvotes,
                    vote_count: newNetVotes
                }
            })

            // Re-sort after vote
            updated.sort((a, b) => {
                if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            })

            logger.debug('Realtime: Vote count updated', {
                pollId,
                choiceId,
                voteType,
                newVoteCount: updated.find(c => c.id === choiceId)?.vote_count
            })

            return updated
        })
    }, [pollId, setChoices])

    const handleVoteUpdated = useCallback((choiceId, oldVoteType, newVoteType) => {
        setChoices(prev => {
            const choice = prev.find(c => c.id === choiceId)
            if (!choice) {
                logger.warn('Realtime: Received vote update for unknown choice', { pollId, choiceId })
                return prev
            }

            const updated = prev.map(c => {
                if (c.id !== choiceId) return c

                let newUpvotes = c.upvote_count || 0
                let newDownvotes = c.downvote_count || 0

                // Remove old vote type, add new vote type
                if (oldVoteType === 'upvote') {
                    newUpvotes = Math.max(0, newUpvotes - 1)
                } else {
                    newDownvotes = Math.max(0, newDownvotes - 1)
                }

                if (newVoteType === 'upvote') {
                    newUpvotes = newUpvotes + 1
                } else {
                    newDownvotes = newDownvotes + 1
                }

                const newNetVotes = newUpvotes - newDownvotes

                return {
                    ...c,
                    upvote_count: newUpvotes,
                    downvote_count: newDownvotes,
                    vote_count: newNetVotes
                }
            })

            // Re-sort after vote update
            updated.sort((a, b) => {
                if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            })

            logger.debug('Realtime: Vote updated (toggle)', {
                pollId,
                choiceId,
                oldVoteType,
                newVoteType,
                newVoteCount: updated.find(c => c.id === choiceId)?.vote_count
            })

            return updated
        })
    }, [pollId, setChoices])

    const handleVoteRemoved = useCallback((choiceId, voteDeviceId, voteType = null) => {
        setChoices(prev => {
            const choice = prev.find(c => c.id === choiceId)
            if (!choice) {
                logger.warn('Realtime: Received vote removal for unknown choice', { pollId, choiceId })
                return prev
            }

            // Check if this is a removal we optimistically updated - if so, skip to prevent double-counting
            const isPendingRemoval = pendingRemovalsRef.current.has(choiceId)

            // Also check if this is our own removal (even if not pending anymore) - skip to prevent double-counting
            const currentDeviceId = deviceIdRef.current
            const isOurRemoval = voteDeviceId && currentDeviceId && voteDeviceId === currentDeviceId

            if (isPendingRemoval || isOurRemoval) {
                logger.debug('Realtime: Ignoring vote removal event', {
                    pollId,
                    choiceId,
                    isPendingRemoval,
                    isOurRemoval,
                    voteDeviceId: voteDeviceId || 'not provided',
                    ourDeviceId: currentDeviceId || 'not set'
                })
                // Remove from pending set since we received the real-time confirmation
                if (isPendingRemoval) {
                    pendingRemovalsRef.current.delete(choiceId)
                }
                return prev
            }

            // This is someone else's vote removal - update count and sync localStorage if needed
            const updated = prev.map(c => {
                if (c.id !== choiceId) return c

                let newUpvotes = c.upvote_count || 0
                let newDownvotes = c.downvote_count || 0

                // If voteType is provided, remove that specific type, otherwise decrement net (fallback)
                if (voteType === 'upvote') {
                    newUpvotes = Math.max(0, newUpvotes - 1)
                } else if (voteType === 'downvote') {
                    newDownvotes = Math.max(0, newDownvotes - 1)
                } else {
                    // Fallback: if voteType unknown, try to infer from current counts
                    if (newUpvotes > 0) {
                        newUpvotes = Math.max(0, newUpvotes - 1)
                    } else if (newDownvotes > 0) {
                        newDownvotes = Math.max(0, newDownvotes - 1)
                    }
                }

                const newNetVotes = newUpvotes - newDownvotes

                return {
                    ...c,
                    upvote_count: newUpvotes,
                    downvote_count: newDownvotes,
                    vote_count: newNetVotes
                }
            })

            // Re-sort after vote removal
            updated.sort((a, b) => {
                if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            })

            const newVoteCount = updated.find(c => c.id === choiceId)?.vote_count || 0

            logger.info('Realtime: Vote removal count updated', {
                pollId,
                choiceId,
                voteType: voteType || 'unknown',
                newVoteCount,
                voteDeviceId: voteDeviceId || 'not provided'
            })

            return updated
        })
    }, [pollId, setChoices])

    /**
     * Process a vote INSERT event from Supabase
     * Includes logic to filter out own votes and track vote ID mappings
     */
    const processVoteInsert = useCallback((payload) => {
        const voteId = payload.new.id
        const voteChoiceId = payload.new.choice_id
        const voteDeviceId = payload.new.device_id
        const voteType = payload.new.vote_type || 'upvote'

        // Track vote ID to choice ID mapping for DELETE events
        if (voteId && voteChoiceId) {
            voteIdToChoiceIdRef.current.set(voteId, voteChoiceId)
            logger.debug('Realtime: Tracked vote ID to choice ID', { voteId, choiceId: voteChoiceId })
        }

        logger.info('Realtime: Vote INSERT event received', {
            pollId,
            choiceId: voteChoiceId,
            voteId: voteId || 'not provided',
            voteDeviceId: voteDeviceId || 'not provided',
            voteType
        })

        // Check if this vote belongs to a choice in our poll using ref to avoid stale closure
        const currentChoices = choicesRef.current
        const choiceExists = currentChoices.some(choice => choice.id === voteChoiceId)

        // Check if this is a vote we optimistically updated - if so, skip to prevent double-counting
        const isPendingVote = pendingVotesRef.current.has(voteChoiceId)

        // Also check if this is our own vote (even if not pending anymore) - skip to prevent double-counting
        const currentDeviceId = deviceIdRef.current
        const isOurVote = voteDeviceId && currentDeviceId && voteDeviceId === currentDeviceId

        if (choiceExists && !isPendingVote && !isOurVote) {
            logger.info('Realtime: New vote cast for our poll (from another browser)', {
                pollId,
                choiceId: voteChoiceId,
                voteType,
                voteDeviceId: voteDeviceId || 'not provided',
                ourDeviceId: currentDeviceId || 'not set'
            })
            handleNewVote(voteChoiceId, voteType)
        } else if (isPendingVote || isOurVote) {
            logger.debug('Realtime: Ignoring vote INSERT for optimistically updated choice or our own vote', {
                pollId,
                choiceId: voteChoiceId,
                isPendingVote,
                isOurVote,
                voteDeviceId: voteDeviceId || 'not provided',
                ourDeviceId: currentDeviceId || 'not set'
            })
            // Remove from pending set since we received the real-time confirmation
            if (isPendingVote) {
                pendingVotesRef.current.delete(voteChoiceId)
            }
        } else {
            // If choices haven't loaded yet, refresh the poll data
            if (currentChoices.length === 0) {
                logger.info('Realtime: Vote received but choices not loaded yet, refreshing poll', {
                    pollId,
                    voteChoiceId
                })
            } else {
                logger.warn('Realtime: Vote for choice not in current poll, ignoring', {
                    pollId,
                    voteChoiceId,
                    ourChoices: currentChoices.map(c => ({ id: c.id, text: c.text }))
                })
            }
        }
    }, [pollId, choicesRef, handleNewVote])

    /**
     * Process a vote UPDATE event from Supabase
     */
    const processVoteUpdate = useCallback((payload) => {
        const voteId = payload.new.id
        const voteChoiceId = payload.new.choice_id
        const voteDeviceId = payload.new.device_id
        const oldVoteType = payload.old?.vote_type
        const newVoteType = payload.new.vote_type || 'upvote'

        logger.info('Realtime: Vote UPDATE event received', {
            pollId,
            choiceId: voteChoiceId,
            voteId: voteId || 'not provided',
            voteDeviceId: voteDeviceId || 'not provided',
            oldVoteType: oldVoteType || 'not provided in payload',
            newVoteType
        })

        // Check if this vote belongs to a choice in our poll
        const currentChoices = choicesRef.current
        const choiceExists = currentChoices.some(choice => choice.id === voteChoiceId)

        // Check if this is a vote we optimistically updated - if so, skip to prevent double-counting
        const isPendingVote = pendingVotesRef.current.has(voteChoiceId)

        // Also check if this is our own vote update - skip to prevent double-counting
        const currentDeviceId = deviceIdRef.current
        const isOurVoteUpdate = voteDeviceId && currentDeviceId && voteDeviceId === currentDeviceId

        // Determine if vote type changed
        const voteTypeChanged = oldVoteType ? oldVoteType !== newVoteType : true

        if (choiceExists && !isPendingVote && !isOurVoteUpdate && voteTypeChanged) {
            let inferredOldVoteType = oldVoteType
            if (!inferredOldVoteType) {
                inferredOldVoteType = newVoteType === 'upvote' ? 'downvote' : 'upvote'
                logger.info('Realtime: Inferring old vote type (not in payload)', {
                    pollId,
                    choiceId: voteChoiceId,
                    inferredOldVoteType,
                    newVoteType
                })
            }

            logger.info('Realtime: Vote type changed for our poll (toggle from another user)', {
                pollId,
                choiceId: voteChoiceId,
                oldVoteType: inferredOldVoteType,
                newVoteType,
                voteDeviceId: voteDeviceId || 'not provided',
                ourDeviceId: currentDeviceId || 'not set'
            })
            handleVoteUpdated(voteChoiceId, inferredOldVoteType, newVoteType)
        } else if (isPendingVote || isOurVoteUpdate) {
            logger.debug('Realtime: Ignoring vote update for optimistically updated choice or our own vote', {
                pollId,
                choiceId: voteChoiceId,
                isPendingVote,
                isOurVoteUpdate,
                voteDeviceId: voteDeviceId || 'not provided',
                ourDeviceId: currentDeviceId || 'not set'
            })
            // Remove from pending set since we received the real-time confirmation
            if (isPendingVote) {
                pendingVotesRef.current.delete(voteChoiceId)
            }
        }
    }, [pollId, choicesRef, handleVoteUpdated])

    /**
     * Process a vote DELETE event from Supabase
     */
    const processVoteDelete = useCallback((payload) => {
        const voteId = payload.old?.id
        const voteChoiceId = payload.old?.choice_id
        const voteDeviceId = payload.old?.device_id
        const voteType = payload.old?.vote_type || null

        logger.info('Realtime: Vote DELETE event received', {
            pollId,
            voteId: voteId || 'not provided',
            choiceId: voteChoiceId || 'not provided',
            deviceId: voteDeviceId || 'not provided',
            voteType: voteType || 'not provided'
        })

        // Supabase DELETE events may only include the primary key (id) in payload.old
        let choiceIdToUse = voteChoiceId
        if (!choiceIdToUse && voteId) {
            const mappedChoiceId = voteIdToChoiceIdRef.current.get(voteId)
            if (mappedChoiceId) {
                logger.info('Realtime: Found choice_id from vote ID mapping', {
                    pollId,
                    voteId,
                    choiceId: mappedChoiceId
                })
                choiceIdToUse = mappedChoiceId
            } else {
                logger.warn('Realtime: DELETE event missing choice_id and no mapping found', {
                    pollId,
                    voteId: voteId || 'unknown'
                })
                return
            }
        }

        if (!choiceIdToUse) {
            logger.warn('Realtime: DELETE event missing choice_id', {
                pollId
            })
            return
        }

        // Check if this vote removal belongs to a choice in our poll
        const currentChoices = choicesRef.current
        const choiceExists = currentChoices.some(choice => choice.id === choiceIdToUse)

        if (choiceExists) {
            logger.info('Realtime: Vote removed for our poll', {
                pollId,
                choiceId: choiceIdToUse,
                currentVoteCount: currentChoices.find(c => c.id === choiceIdToUse)?.vote_count || 0,
                voteDeviceId: voteDeviceId || 'not provided',
                ourDeviceId: deviceIdRef.current || 'not set'
            })

            handleVoteRemoved(choiceIdToUse, voteDeviceId, voteType)
            // Clean up vote ID mapping if we have it
            if (voteId) {
                voteIdToChoiceIdRef.current.delete(voteId)
            }
        } else {
            logger.warn('Realtime: Vote removal for choice not in current poll, ignoring', {
                pollId,
                choiceId: choiceIdToUse
            })
            // Clean up vote ID mapping even if choice not found
            if (voteId) {
                voteIdToChoiceIdRef.current.delete(voteId)
            }
        }
    }, [pollId, choicesRef, handleVoteRemoved])

    return {
        handleNewVote,
        handleVoteUpdated,
        handleVoteRemoved,
        processVoteInsert,
        processVoteUpdate,
        processVoteDelete,
        pendingVotesRef,
        pendingRemovalsRef,
        voteIdToChoiceIdRef
    }
}

export default useRealtimeVotes
